import { useTranslation } from 'react-i18next'

import { Accordion } from '@/modules/shared/components/accordion/Accordion'
import { useGetProductModalBarcodes } from '@/modules/shared/components/product-modal/hooks/useGetProductModalBarcodes'
import { QueryResult } from '@/modules/shared/components/query-result/QueryResult'
import Table from '@/modules/shared/components/table/Table'
import { useViewType } from '@/modules/shared/hooks/useViewType'

interface ProductModalBarcodeDetailsProps {
  productId: number
  barcodesTotalCount: number
}

export default function ProductModalBarcodeDetails({ productId, barcodesTotalCount }: ProductModalBarcodeDetailsProps) {
  const { t } = useTranslation()
  const { isUtility } = useViewType()

  const { fetchProductModalBarcodes, barcodes, loadingStates, error } = useGetProductModalBarcodes({
    productId,
  })

  const handleAccordionOpen = () => {
    if (!barcodes && barcodesTotalCount > 0) {
      fetchProductModalBarcodes()
    }
  }

  return (
    <Accordion
      title={t('general.barcodes', 'Barcodes')}
      buttonClassName="px-3 h-11 mt-2.5"
      titleClassName="font-bold"
      titleEndIcon={
        <div className="flex size-5 items-center justify-center rounded-md bg-gray-400 text-xs text-white">
          {barcodesTotalCount}
        </div>
      }
      onOpen={handleAccordionOpen}
    >
      <div className="mb-5">
        <p className="my-3 text-sm">
          {t(
            'productModal.barcodesDescription',
            'Your Organisation may add Barcodes to a Product so that the Barcode can be used to search for the Product while creating a Requisition, Receiving Goods or performing a Stocktake. '
          )}
        </p>
        <QueryResult loading={loadingStates.loading} error={error}>
          <Table
            columns={[
              {
                title: t('general.barcode', 'Barcode'),
                key: 'barcodes',
                headerCellStyles: 'pl-4 py-3',
                cellStyles: 'pl-4 py-3',
                minWidth: 13,
                grow: true,
                header: {
                  tooltip: t('product.barcode.barcodeTooltip', 'The Body of the Barcode.'),
                },
                render: ({ body }) => body,
              },
              {
                title: t('general.ownerID', 'Owner ID'),
                key: 'ownerID',
                headerCellStyles: 'py-3 text-center',
                cellStyles: 'py-3 text-center',
                minWidth: 6,
                maxWidth: 6,
                hidden: !isUtility,
                header: {
                  tooltip: t(
                    'product.barcode.ownerIDTooltip',
                    'The ID of the Organisation that owns and manages this Barcode.'
                  ),
                },
                render: ({ organisation }) => organisation.id,
              },
            ]}
            dataSource={barcodes || []}
            keyExtractor={(record) => String(record.id)}
          />
          {(barcodes?.length === 0 || barcodesTotalCount === 0) && (
            <div className="border p-4 text-center text-gray-500">
              {t('product.barcodes.emptyBarcodes2', 'There are no Barcodes created for this product.')}
            </div>
          )}
        </QueryResult>
      </div>
    </Accordion>
  )
}
